import { useCallback, useEffect, useRef } from 'react'

import { formatISO } from 'date-fns'
import { createAccessLog } from 'integration/resources/accessLogs'
import { CreateAccessLogPayload } from 'integration/resources/accessLogs/types'
import { Platform } from 'react-native'

type TrackingState = CreateAccessLogPayload & { startedAt: number }

type UseAccessLogsParams = {
  initialPageName?: string
  initialPagePath?: string
}

type RegisterNavigationOptions = {
  trackNextPage?: boolean
}

export const useAccessLogs = ({ initialPageName, initialPagePath }: UseAccessLogsParams) => {
  const trackingRef = useRef<TrackingState>()

  const startTracking = useCallback((pageName: string, pagePath: string) => {
    if (Platform.OS !== 'web') return

    trackingRef.current = {
      page_name: pageName,
      page_path: pagePath,
      device: 'desktop',
      accessed_at: formatISO(new Date()),
      duration_seconds: 0,
      startedAt: Date.now()
    }
  }, [])

  const registerNavigation = useCallback(
    async (nextPageName: string, nextPagePath: string, options?: RegisterNavigationOptions) => {
      if (Platform.OS !== 'web' || !nextPagePath) return

      const shouldTrackNextPage = options?.trackNextPage ?? true

      const current = trackingRef.current

      const now = Date.now()

      if (!current) {
        if (shouldTrackNextPage) {
          startTracking(nextPageName, nextPagePath)
        }

        return
      }

      if (current.page_path === nextPagePath) return

      if (current) {
        const durationSeconds = Math.max(1, Math.round((now - current.startedAt) / 1000))

        try {
          await createAccessLog({
            page_name: current.page_name,
            page_path: current.page_path,
            device: current.device,
            accessed_at: current.accessed_at,
            duration_seconds: durationSeconds
          })
        } catch (error) {
          console.error('[access-log] Failed to create access log', error)
        }
      }

      if (shouldTrackNextPage) {
        startTracking(nextPageName, nextPagePath)
      } else {
        startTracking(current.page_name, current.page_path)
      }
    },
    [startTracking]
  )

  useEffect(() => {
    if (Platform.OS !== 'web') return

    const pagePath =
      initialPagePath ?? (typeof window !== 'undefined' ? window.location.pathname : '')

    const pageName = initialPageName ?? pagePath

    if (!pagePath) return

    startTracking(pageName, pagePath)
  }, [initialPageName, initialPagePath, startTracking])

  return { registerNavigation }
}
